import {AbstractInteractionMode, DrawStyleUtil} from "./abstract-interaction-mode";
import {GeometryProvider} from "../providers/geometry-provider";
import {Layer, LayerGeometry} from "../../document/model/layer";
import {MapEvent} from "../../document/model/map";
import {MenuItem} from "primevue/menuitem";
import {ViewSimpleSelect} from "../interaction/view/view-simple-select";
import store from "../../../js/store";
import MapLayerSelectionDialog from "../../../components/map/MapLayerSelectionDialog.vue"
import ExportGeometryDialog from '../../../components/dialogs/ExportGeometry.vue'

import {DynamicDialogCloseOptions} from "primevue/dynamicdialogoptions";
import {RendererUtils} from "../renderer/renderer-utils";
import {defineAsyncComponent} from "vue";
import {ExportTypeEnum} from "../../../js/enums/export-type";

import {BoundingBoxSimpleSelect} from '../interaction/bounding-box/bounding-box-simple-select';

let MapExportAttributesDialog = defineAsyncComponent(() => import("../../../components/map/dialogs/MapExportAttributesDialog.vue"));
let MapCopyAttributesDialog = defineAsyncComponent(() => import("../../../components/map/dialogs/MapCopyAttributesDialog.vue"));

export class ViewMode extends AbstractInteractionMode {
  static identifier: string = "view";
  static title: string = "Vizualizează";
  static icon: string = "/icons/toolbox/eye.svg";
  static allowPriorityContextMenu: boolean = true;
  static drawStyles: any = DrawStyleUtil.drawStyle({
    "line-color": '#23B402',
    "border-color": '#23B402',
    "line-width": 5
  });

  static interactions = {
    'select': ViewSimpleSelect.definition({
      selectionAllowed: true,
      multiSelection: true,
      priorityContextActions: true
    }),
    "bound-box-select": BoundingBoxSimpleSelect.definition({
      selectionAllowed: true,
      multiSelection: true,
      priorityContextActions: true,
    }),
  }

  public _intersectButtonSubscriber;

  onInit() {
    super.onInit();
    this.setupGeometryIntersectionHandler();
  }

  /*getLayer(): Layer | undefined {
    return undefined;
  }*/

  getGlobalActions(interactionIdentifier: string, point: mapboxgl.Point | undefined = undefined): MenuItem[] {
    if (point !== undefined) {
      let uniqueIds = {};
      this._mapInstance.queryRenderedFeatures(point, {layers: this.controller.getSearchableStyleLayerIds()}).forEach(feature => {
        if (uniqueIds.hasOwnProperty(feature.id) || this.controller.interaction.isFocused({geometryId: feature.id} as LayerGeometry)) {
          return;
        }

        uniqueIds[feature.id] = feature;
      });

      if ((Object.keys(uniqueIds).length > 0 && !this.controller?.interaction?.mode?.layer?.documentId)
        ||
        (Object.keys(uniqueIds).find(x => x === this.controller?.interaction?.mode?.layer?.documentId))
      ) {
        return [
          {
            label: 'Alternative select',
            icon: "/icons/toolbox/layers.svg",
            items: Object.keys(uniqueIds).map(uniqueId => {
              let feature = uniqueIds[uniqueId];
              let layer = this.controller.getLayerFromStyleLayerId(feature.layer.id);
              return {
                label: layer?.properties?.intersectionLabel?.interpolateObject({...feature, layer}) || layer.title,
                command: async () => {

                  let layerGeometry = await GeometryProvider.fetchSingleGeometry(feature.id, layer, this.controller.configuration.accessToken);
                  this.controller.interaction.focusSingle(layerGeometry);
                }
              };
            })?.filter(x => x)
          }
        ]
      }

    }
    return [];
  }

  getSingleActions(feature, drawMode, point: mapboxgl.Point | undefined = undefined): MenuItem[] {
    const featureTemporary = feature.layer?.id?.includes('gl-draw');
    let actions: MenuItem[] = [];

    if (this.hasAccessTo('intersection', 'map') && !featureTemporary) {
      actions.push({
        label: "Select intersections",
        icon: "/icons/toolbox/select-intersection.svg",
        command: async () => this._mapInstance.fire(MapEvent.LayerGeometryIntersect, {feature})
      })

    }

    if (this.hasAccessTo('export', 'map') && !featureTemporary) {
      actions.push({
        label: 'Export attributes',
        icon: "/icons/toolbox/attributes-export.svg",
        contextMenuOnly: true,
        command: () => {
          this.controller.configuration.dialogProvider().open(
            MapExportAttributesDialog,
            {
              props: {
                header: this.controller.configuration.translatorProvider('Export attributes'),
                style: {
                  "width": "500px",
                  "min-width": '200px',
                  "min-height": "200px",
                  "resize": "both",
                  "overflow": "hidden"
                },
              },
              data: {
                mapController: this.controller,
                layerGeometry: feature
              }
            },
          )
        }
      });
    }

    return actions;
  }

  getMultiActions(features, drawMode): MenuItem[] {
    const allFeaturesTemporary = features.findIndex(f => !f.layer?.id?.includes('gl-draw')) < 0;
    if (!allFeaturesTemporary) features = features.filter(f => !f.layer?.id?.includes('gl-draw'));
    //@ts-ignore
    let geometryTypes = [...new Set(features?.map(feature => feature?.geometry?.geometry?.type))];

    let actions: MenuItem[] = [];

    const visibleLayers = this.controller.getLayers().filter(l => {
      return this.controller.getStyleLayerIds(l).findIndex(sl => this._mapInstance.getLayoutProperty(sl, 'visibility') === 'none') < 0;
    });

    let exportItem: MenuItem = {
      label: allFeaturesTemporary ? 'Export intersections' : 'Export',
      icon: '/icons/toolbox/cloud-download.svg',
      contextMenuOnly: true,
      items: [
        {
          label: '.SHP File',
          icon: "/icons/toolbox/shape.svg",
          command: () => {
            this.controller.configuration.dialogProvider().open(
              ExportGeometryDialog,
              {
                props: {
                  header: this.controller.configuration.translatorProvider(allFeaturesTemporary ? 'Export intersections' : 'Export'),
                  style: {
                    "min-width": '300px',
                    "resize": "both",
                    "overflow": "hidden"
                  }
                },
                data: {
                  exportDispatchObject: {
                    query: {
                      moduleId: this.controller.configuration.moduleId,
                      exportType: ExportTypeEnum.SHP
                    },
                    data: allFeaturesTemporary ? {
                      intersections: features.map(f => f._geometry),
                      documentIds: visibleLayers.map(vl => vl.documentId)
                    } : {
                      geometries: features.map(f => ({geometryId: f.geometryId, documentId: f.documentId}))
                    }
                  },
                  mapController: this.controller
                }
              }
            );
          }
        },
        {
          label: '.Geojson File',
          icon: "/icons/toolbox/shape.svg",
          command: () => {
            this.controller.configuration.dialogProvider().open(
              ExportGeometryDialog,
              {
                props: {
                  header: this.controller.configuration.translatorProvider(allFeaturesTemporary ? 'Export intersections' : 'Export'),
                  style: {
                    "min-width": '300px',
                    "resize": "both",
                    "overflow": "hidden"
                  }
                },
                data: {
                  exportDispatchObject: {
                    query: {
                      moduleId: this.controller.configuration.moduleId,
                      exportType: ExportTypeEnum.GEOJSON
                    },
                    data: allFeaturesTemporary ? {
                      intersections: features.map(f => f._geometry),
                      documentIds: visibleLayers.map(vl => vl.documentId)
                    } : {
                      geometries: features.map(f => ({geometryId: f.geometryId, documentId: f.documentId}))
                    }
                  },
                  mapController: this.controller
                }
              }
            );
          }
        },
        {
          label: '.Dxf File',
          icon: "/icons/toolbox/shape.svg",
          command: () => {
            this.controller.configuration.dialogProvider().open(
              ExportGeometryDialog,
              {
                props: {
                  header: this.controller.configuration.translatorProvider(allFeaturesTemporary ? 'Export intersections' : 'Export'),
                  style: {
                    "min-width": '300px',
                    "resize": "both",
                    "overflow": "hidden"
                  }
                },
                data: {
                  exportDispatchObject: {
                    query: {
                      moduleId: this.controller.configuration.moduleId,
                      exportType: ExportTypeEnum.DXF
                    },
                    data: allFeaturesTemporary ? {
                      intersections: features.map(f => f._geometry),
                      documentIds: visibleLayers.map(vl => vl.documentId)
                    } : {
                      geometries: features.map(f => ({geometryId: f.geometryId, documentId: f.documentId}))
                    }
                  },
                  mapController: this.controller
                }
              }
            );
          }
        },
        {
          label: '.Gpkg File',
          icon: "/icons/toolbox/shape.svg",
          command: () => {
            this.controller.configuration.dialogProvider().open(
              ExportGeometryDialog,
              {
                props: {
                  header: this.controller.configuration.translatorProvider(allFeaturesTemporary ? 'Export intersections' : 'Export'),
                  style: {
                    "min-width": '300px',
                    "resize": "both",
                    "overflow": "hidden"
                  }
                },
                data: {
                  exportDispatchObject: {
                    query: {
                      moduleId: this.controller.configuration.moduleId,
                      exportType: ExportTypeEnum.GEOPACKAGE
                    },
                    data: allFeaturesTemporary ? {
                      intersections: features.map(f => f._geometry),
                      documentIds: visibleLayers.map(vl => vl.documentId)
                    } : {
                      geometries: features.map(f => ({geometryId: f.geometryId, documentId: f.documentId}))
                    }
                  },
                  mapController: this.controller
                }
              }
            );
          }
        }
      ]
    };

    if (this.hasAccessTo('export', 'map') && (!allFeaturesTemporary || visibleLayers.length > 0)) {
      actions = [exportItem];
    }

    if (allFeaturesTemporary) return actions;

    let sourceLayer = features[0].document;


    if (geometryTypes.length == 1 && !allFeaturesTemporary) {
      exportItem.items.unshift(
        {
          label: 'Existing layer',
          icon: '/icons/toolbox/layers.svg',
          command: () => {
            this.controller.configuration.dialogProvider().open(
              MapLayerSelectionDialog,
              {
                props: {
                  header: this.controller.configuration.translatorProvider('Export to existing layer'),
                  style: {
                    "width": "500px",
                    "min-width": '200px',
                    "min-height": "200px",
                    "resize": "both",
                    "overflow": "hidden"
                  }
                },
                data: {
                  mapController: this.controller,
                  compatibilityFn: (layer: Layer) => {
                    return [
                      RendererUtils.DocumentTypes.VectorTiles,
                      RendererUtils.DocumentTypes.Vector,
                    ].indexOf(layer.documentCategory.documentCategoryId as number) >= 0
                      && layer.properties?.shapeType?.shapeTypeId === sourceLayer.properties?.shapeType?.shapeTypeId;
                  }
                },
                onClose: (options: DynamicDialogCloseOptions) => {
                  if (options.data?.sourceLayer === undefined) {
                    return;
                  }
                    let endpointData = features.map(el => {

                    // OBOSLATE. We dont want to import all geometry data
                    // return {
                    //   documentId: options.data?.sourceLayer?.documentId,
                    //   geometry: el.geometry,
                    //   attributes: el.attributes ?? {}
                    // }

                    return {
                      geometryId: null,
                      documentId: options.data?.sourceLayer?.documentId,
                      geometry: {
                        geometry: el.geometry.geometry,
                        properties: el.geometry.properties,
                        type: 'Feature',
                      },
                      attributes: el.geometry.properties,
                      properties: el.geometry.properties,
                    };

                  });

                  store.dispatch('saveBulkGeometries', {
                    data: endpointData,
                    headers: {
                      'x-access-token': this.controller.configuration.accessToken
                    }
                  }).then(response => {
                    this.controller.configuration.toastProvider({
                      severity: "success",
                      summary: this.controller.configuration.translatorProvider("Done"),
                      detail: this.controller.configuration.translatorProvider('Successfully saved!'),
                      life: 5000,
                    });
                    if (this.controller.hasLayer(options.data?.sourceLayer)) {
                      this.controller.reloadLayer(options.data?.sourceLayer);
                    }
                  }).catch(error => {
                    this.controller.configuration.toastProvider({
                      severity: "error",
                      summary: "Error",
                      detail: this.controller.configuration.translatorProvider(error),
                      life: 5000,
                    });
                  })
                }
              }
            )
          }
        },)
    }

    if (geometryTypes.length == 1 && geometryTypes[0] === 'LineString') {
      exportItem.items.push({
        label: 'Polygon',
        icon: '/icons/toolbox/layers.svg',
        command: () => {
          this.controller.configuration.dialogProvider().open(
            MapLayerSelectionDialog,
            {
              props: {
                header: this.controller.configuration.translatorProvider('Export to existing layer'),
                style: {
                  "width": "500px",
                  "min-width": '200px',
                  "min-height": "200px",
                  "resize": "both",
                  "overflow": "hidden"
                }
              },
              data: {
                mapController: this.controller,
                compatibilityFn: (layer: Layer) => {
                  return [
                    RendererUtils.DocumentTypes.VectorTiles,
                    RendererUtils.DocumentTypes.Vector,
                  ].indexOf(layer.documentCategory.documentCategoryId as number) >= 0
                    && layer.properties?.shapeType?.shapeTypeId === 1;
                }
              },
              onClose: (options: DynamicDialogCloseOptions) => {
                if (options.data?.sourceLayer === undefined) {
                  return;
                }
                let endpointData = features.map(el => {
                  return {
                    documentId: options.data?.sourceLayer?.documentId,
                    geometry: {
                      type: 'Feature',
                      geometry: {
                        type: 'Polygon',
                        coordinates: [[...el.geometry.geometry.coordinates, el.geometry.geometry.coordinates[0]]]
                      },
                      properties: el.geometry.properties ?? {}
                    },
                    attributes: el.attributes ?? {}
                  }
                });

                store.dispatch('saveBulkGeometries', {
                  data: endpointData,
                  headers: {
                    'x-access-token': this.controller.configuration.accessToken
                  }
                }).then(response => {
                  this.controller.configuration.toastProvider({
                    severity: "success",
                    summary: this.controller.configuration.translatorProvider("Done"),
                    detail: this.controller.configuration.translatorProvider('Successfully saved!'),
                    life: 5000,
                  });
                  if (this.controller.hasLayer(options.data?.sourceLayer)) {
                    this.controller.reloadLayer(options.data?.sourceLayer);
                  }
                }).catch(error => {
                  this.controller.configuration.toastProvider({
                    severity: "error",
                    summary: "Error",
                    detail: this.controller.configuration.translatorProvider(error),
                    life: 5000,
                  });
                })
              }
            }
          )
        }
      });
    }

    /*if (
        geometryTypes.length == 1 && (geometryTypes.includes('Polygon') || geometryTypes.includes('MultiPolygon'))
        || (geometryTypes.length == 2 && geometryTypes.includes('Polygon') && geometryTypes.includes('MultiPolygon'))
    ) {
        actions.push({
            label: 'Clip',
            icon: '/icons/toolbox/clip.svg',
            contextMenuOnly: true,
            command: () => {
                this.controller.configuration.dialogProvider().open(
                    MapLayerSelectionDialog,
                    {
                        props: {
                            header: this.controller.configuration.translatorProvider('Choose layer to clip'),
                            style: {
                                "width": "500px",
                                "min-width": '200px',
                                "min-height": "200px",
                                "resize": "both",
                                "overflow": "hidden"
                            }
                        },
                        data: {
                            mapController: this.controller,
                            layers: this.controller.getLayers().filter(x => x.documentId !== sourceLayer.documentId),
                            compatibilityFn: (layer: Layer) => {
                                return [
                                    RendererUtils.DocumentTypes.Raster,
                                    RendererUtils.DocumentTypes.MBTilesCollection,
                                    RendererUtils.DocumentTypes.Georeference,
                                ].indexOf(layer.documentCategory.documentCategoryId as number) >= 0;
                            }
                        },
                        onClose: (options: DynamicDialogCloseOptions) => {
                            if (options.data === undefined) {
                                return;
                            }

                            let endpointData = {
                                documentId: options.data.documentId,
                                geometries: features
                            }


                            //console.log('todo: Clipping', options.data, 'with', features);

                            store.dispatch('clipByGeometries', {
                                data: endpointData
                            }).then(response => {
                                this.controller.configuration.toastProvider({
                                    severity: "success",
                                    summary: this.controller.configuration.translatorProvider("Done"),
                                    detail: this.controller.configuration.translatorProvider('Successfully saved!'),
                                    life: 5000,
                                });
                                if (this.controller.hasLayer(options.data)) {
                                    this.controller.reloadLayer(options.data);
                                }
                            }).catch(error => {
                                this.controller.configuration.toastProvider({
                                    severity: "error",
                                    summary: "Error",
                                    detail: this.controller.configuration.translatorProvider(error),
                                    life: 5000,
                                });
                            })

                        }
                    }
                )
            }
        })
    }*/


    if (
      features?.length > 1
    ) {
      actions.push({
        label: 'Copy attributes',
        icon: '/icons/toolbox/clone-geometry.svg',
        contextMenuOnly: true,
        command: () => {
          this.controller.configuration.dialogProvider().open(
            MapCopyAttributesDialog,
            {
              props: {
                header: this.controller.configuration.translatorProvider('Copy attributes'),
                style: {
                  "width": "500px",
                  "min-width": '200px',
                  "min-height": "200px",
                  "resize": "both",
                  "overflow": "hidden"
                },
              },
              data: {
                mapController: this.controller,
                geometries: features
              }
            },
          )
        }
      })
    }

    if (features?.length) {
      actions.push({
        label: 'Hide others',
        icon: '/icons/toolbox/hide.svg',
        contextMenuOnly: true,
        command: () => {
          let distinctDocumentIds = [...new Set(features?.map(x => x.documentId))]
          let layers: any = this.controller.getLayers().filter(f => distinctDocumentIds.includes(f.documentId));
          this.controller.interaction.addVisibleFeatures(features)
          layers.forEach(layer => {
            let condition: any = ["all"];
            if (this.controller.interaction.getVisibleFeatures().length)
              condition.push(["in", "$id", ...this.controller.interaction.getVisibleFeatures().filter(f => f.documentId === layer.documentId).map(x => x.geometryId)])
            if (this.controller.interaction.getHiddenFeatures().length)
              condition.push(["!in", "$id", ...this.controller.interaction.getHiddenFeatures().filter(f => f.documentId === layer.documentId).map(x => x.geometryId)])
            //["all",["in", "$id", ...this.controller.interaction.getVisibleFeatures().filter(f => f.documentId === layer.documentId).map(x => x.geometryId)],["!in", "$id", ...this.controller.interaction.getHiddenFeatures().filter(f => f.documentId === layer.documentId).map(x => x.geometryId)]]
            if (this.controller) this.controller.setLayerFilters(layer, condition);
          })
          this.controller.mapInstance.fire(MapEvent.Tick);
        }
      })

      actions.push({
        label: 'Hide',
        icon: '/icons/toolbox/hide.svg',
        contextMenuOnly: true,
        command: () => {
          let distinctDocumentIds = [...new Set(features?.map(x => x.documentId))]
          let layers: any = this.controller.getLayers().filter(f => distinctDocumentIds.includes(f.documentId));
          this.controller.interaction.addHiddenFeatures(features)
          layers.forEach(layer => {
            let condition: any = ["all"];
            if (this.controller.interaction.getVisibleFeatures().length)
              condition.push(["in", "$id", ...this.controller.interaction.getVisibleFeatures().filter(f => f.documentId === layer.documentId).map(x => x.geometryId)])
            if (this.controller.interaction.getHiddenFeatures().length)
              condition.push(["!in", "$id", ...this.controller.interaction.getHiddenFeatures().filter(f => f.documentId === layer.documentId).map(x => x.geometryId)])
            if (this.controller) this.controller.setLayerFilters(layer, condition);
          })
          this.controller.mapInstance.fire(MapEvent.Tick);
        }
      })

    }


    return actions;

  }

  private setupGeometryIntersectionHandler(): void {
    if (!this._intersectButtonSubscriber) {
      this._intersectButtonSubscriber = (event: {feature: LayerGeometry}) => {
        GeometryProvider.fetchIntersectedGeometries(
          [event.feature.geometryId],
          this.controller.getLayerMap(),
          {
            relatedDocumentIds: this.controller.getLayers().map(layer => layer.documentId),
            "x-access-token": this.controller.configuration.accessToken
          }
        )
          .then(intersections => {
            this.controller.interaction.setFocus(
              intersections.map(intersectedGeometry => {
                intersectedGeometry.document = this.controller.getLayer(intersectedGeometry.documentId);
                return intersectedGeometry;
              })
            );
            this.controller.interaction.mode.resetInteraction();
            this.controller.interaction._notifyFocus();
            this.controller.mapInstance.fire(MapEvent.Tick);

          });
      }
      this._mapInstance.on(MapEvent.LayerGeometryIntersect, this._intersectButtonSubscriber);
    }
  }

  onDestroy(): void {
    super.onDestroy();
    this._mapInstance.off(MapEvent.LayerGeometryIntersect, this._intersectButtonSubscriber);
  }
}
